import { useEffect, useState, FormEvent } from 'react';
import Section from '../../components/base/Section';
import Button from '../../components/base/Button';

export default function ContactoPage() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    servicio: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Contacto Vaciado Pisos Granollers | JUSTCLEAN ☎ 602 36 59 96';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contacta con JUSTCLEAN para vaciado de pisos en Granollers. Presupuesto gratis en 24h. ☎ 602 36 59 96 | WhatsApp | info@granollers.justclean.es');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'contacto vaciado Granollers, presupuesto gratis, teléfono, email, WhatsApp');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://granollers.justclean.es'}/contacto`);
    }

    // Schema.org JSON-LD - ContactPage
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contacto JUSTCLEAN GRANOLLERS",
      "description": "Contacta con nosotros para solicitar presupuesto sin compromiso",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://granollers.justclean.es'}/contacto`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "JUSTCLEAN GRANOLLERS",
        "telephone": "+34602365996",
        "email": "info@granollers.justclean.es",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Carrer de Girona, 45",
          "addressLocality": "Granollers",
          "addressRegion": "Barcelona",
          "postalCode": "08402",
          "addressCountry": "ES"
        }
      }
    });
    document.head.appendChild(schemaScript);

    return () => {
      if (schemaScript.parentNode) {
        schemaScript.parentNode.removeChild(schemaScript);
      }
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4q2es2r20r5k3vfkmlg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          servicio: '',
          mensaje: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <main className="overflow-x-hidden">
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/10b4f2af20380ec285b079eb488dbc9c.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/40"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 max-w-7xl text-center text-white">
          <nav className="text-sm mb-4 opacity-90">
            <a href="/" className="hover:text-primary transition-colors cursor-pointer">Inicio</a>
            <span className="mx-2">&gt;</span>
            <span>Contacto</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 px-4">
            Contacta con JUSTCLEAN Granollers
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-3xl mx-auto px-4">
            Presupuesto sin compromiso en menos de 24 horas. Estamos aquí para ayudarte.
          </p>
        </div>
      </section>

      {/* Formulario y Datos de Contacto */}
      <Section background="white" padding="lg">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Columna Izquierda - Información */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-secondary mb-6">
              Información de contacto
            </h2>
            <p className="text-tertiary mb-8 leading-relaxed text-sm sm:text-base break-words">
              Estamos disponibles 24/7 para atender tus consultas sobre <a href="/servicios#vaciado-pisos" className="text-primary underline hover:text-secondary cursor-pointer">vaciado de pisos</a>, <a href="/servicios#limpieza-diogenes" className="text-primary underline hover:text-secondary cursor-pointer">limpieza Diógenes</a> y todos nuestros <a href="/servicios" className="text-primary underline hover:text-secondary cursor-pointer">servicios</a> en Granollers y Vallès Oriental.
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-primary text-2xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="font-bold text-secondary mb-1 text-sm sm:text-base">Dirección</h3>
                  <p className="text-tertiary text-sm break-words">
                    Carrer de Girona, 45<br />
                    08402 Granollers, Barcelona
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-primary text-2xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="font-bold text-secondary mb-1 text-sm sm:text-base">Teléfono</h3>
                  <a href="tel:+34602365996" className="text-primary font-semibold text-base sm:text-lg hover:underline cursor-pointer break-words">
                    602 36 59 96
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-primary text-2xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="font-bold text-secondary mb-1 text-sm sm:text-base">Email</h3>
                  <a href="mailto:info@granollers.justclean.es" className="text-primary hover:underline cursor-pointer text-sm break-all">
                    info@granollers.justclean.es
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-primary text-2xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <div>
                  <h3 className="font-bold text-secondary mb-1 text-sm sm:text-base">Horario</h3>
                  <p className="text-tertiary text-sm">
                    Servicio 24/7<br />
                    Disponible todos los días del año
                  </p>
                </div>
              </div>
            </div>

            {/* Mapa */}
            <div className="rounded-2xl overflow-hidden shadow-lg mb-8">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2985.8!2d2.287!3d41.6077!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDM2JzI3LjciTiAywrAxNycxMy4yIkU!5e0!3m2!1ses!2ses!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación JUSTCLEAN Granollers"
              ></iframe>
            </div>

            {/* Zona de Servicio */}
            <div className="bg-light rounded-2xl p-4 sm:p-6">
              <h3 className="font-bold text-secondary mb-3 flex items-center gap-2 text-sm sm:text-base">
                <i className="ri-map-2-line text-primary w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                Zona de Servicio
              </h3>
              <p className="text-sm text-tertiary leading-relaxed break-words">
                Granollers, Canovelles, Les Franqueses del Vallès, La Roca del Vallès, Montmeló, Parets del Vallès, Cardedeu, Mollet del Vallès, La Garriga, Lliçà d'Amunt, Lliçà de Vall y toda la comarca del Vallès Oriental. Conoce más sobre <a href="/empresa" className="text-primary underline hover:text-secondary cursor-pointer">nuestra empresa</a>.
              </p>
            </div>
          </div>

          {/* Columna Derecha - Formulario */}
          <div className="lg:col-span-3">
            <div className="bg-light rounded-2xl p-6 sm:p-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-secondary mb-6">
                Solicita tu presupuesto gratis
              </h2>
              <p className="text-tertiary mb-8 text-sm sm:text-base break-words">
                Rellena el formulario y te responderemos en menos de 24 horas con un presupuesto personalizado sin compromiso. Si tienes dudas, consulta nuestras <a href="/faq" className="text-primary underline hover:text-secondary cursor-pointer">preguntas frecuentes</a>.
              </p>

              <form onSubmit={handleSubmit} data-readdy-form id="contacto-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-semibold text-secondary mb-2">
                      Nombre completo *
                    </label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border-2 border-tertiary/30 focus:border-primary focus:outline-none transition-colors duration-300 text-sm"
                      placeholder="Tu nombre"
                    />
                  </div>

                  <div>
                    <label htmlFor="telefono" className="block text-sm font-semibold text-secondary mb-2">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      id="telefono"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border-2 border-tertiary/30 focus:border-primary focus:outline-none transition-colors duration-300 text-sm"
                      placeholder="602 36 59 96"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="email" className="block text-sm font-semibold text-secondary mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 border-tertiary/30 focus:border-primary focus:outline-none transition-colors duration-300 text-sm"
                    placeholder="tu@email.com"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="servicio" className="block text-sm font-semibold text-secondary mb-2">
                    Servicio que necesitas *
                  </label>
                  <select
                    id="servicio"
                    name="servicio"
                    value={formData.servicio}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border-2 border-tertiary/30 focus:border-primary focus:outline-none transition-colors duration-300 text-sm cursor-pointer"
                  >
                    <option value="">Selecciona un servicio</option>
                    <option value="Vaciado de Pisos">Vaciado de Pisos y Casas</option>
                    <option value="Vaciado de Locales">Vaciado de Locales Comerciales</option>
                    <option value="Retirada de Muebles">Retirada de Muebles y Electrodomésticos</option>
                    <option value="Limpieza Diógenes">Limpieza por Síndrome de Diógenes</option>
                    <option value="Pintura">Pintura Final</option>
                    <option value="Reparaciones">Pequeñas Reparaciones</option>
                    <option value="Varios Servicios">Varios Servicios</option>
                    <option value="Consulta General">Consulta General</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="mensaje" className="block text-sm font-semibold text-secondary mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg border-2 border-tertiary/30 focus:border-primary focus:outline-none transition-colors duration-300 resize-none text-sm"
                    placeholder="Cuéntanos qué necesitas... (máximo 500 caracteres)"
                  ></textarea>
                  <p className="text-xs text-tertiary mt-1">
                    {formData.mensaje.length}/500 caracteres
                  </p>
                </div>

                <div className="mb-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      className="mt-1 w-5 h-5 text-primary border-tertiary/30 rounded focus:ring-primary cursor-pointer"
                    />
                    <span className="text-sm text-tertiary">
                      Acepto la <a href="#" className="text-primary underline hover:text-secondary">Política de Privacidad</a> y el tratamiento de mis datos para recibir información sobre los servicios de JUSTCLEAN GRANOLLERS.
                    </span>
                  </label>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
                  <i className="ri-send-plane-line w-5 h-5 flex items-center justify-center"></i>
                </Button>

                {submitStatus === 'success' && (
                  <div className="mt-4 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg">
                    <p className="text-green-700 font-semibold">
                      ¡Mensaje enviado con éxito! Te responderemos en menos de 24 horas.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
                    <p className="text-red-700 font-semibold">
                      Error al enviar el mensaje. Por favor, llámanos al 602 36 59 96.
                    </p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </Section>

      {/* Beneficios Recordatorio */}
      <Section background="light" padding="lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <i className="ri-file-list-3-line text-5xl text-primary"></i>
            </div>
            <h3 className="text-xl font-bold text-secondary mb-2">Presupuesto gratis</h3>
            <p className="text-tertiary">
              Sin compromiso y completamente personalizado a tus necesidades
            </p>
          </div>

          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <i className="ri-time-line text-5xl text-primary"></i>
            </div>
            <h3 className="text-xl font-bold text-secondary mb-2">Respuesta en 24h</h3>
            <p className="text-tertiary">
              Te contactamos en menos de 24 horas con tu presupuesto
            </p>
          </div>

          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <i className="ri-shield-check-line text-5xl text-primary"></i>
            </div>
            <h3 className="text-xl font-bold text-secondary mb-2">Sin compromiso</h3>
            <p className="text-tertiary">
              Solicita información sin ningún tipo de obligación
            </p>
          </div>
        </div>
      </Section>

      {/* CTA Final */}
      <Section background="gradient" padding="lg">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold mb-6">
            Llama ahora y resuelve tu vaciado hoy
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Atención inmediata 24/7 en Granollers y Vallès Oriental
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="white"
              size="lg"
              onClick={() => window.location.href = 'tel:+34602365996'}
              icon={<i className="ri-phone-line"></i>}
            >
              Llamar 602 36 59 96
            </Button>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => window.open('https://wa.me/34602365996', '_blank')}
              icon={<i className="ri-whatsapp-line"></i>}
              className="border-white text-white hover:bg-white hover:text-primary"
            >
              WhatsApp
            </Button>
          </div>
        </div>
      </Section>
    </main>
  );
}
